import { formatDate, formatTime } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import type { IndividualMessage } from '@/shared/types';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';

interface IndividualMessagesTimelineProps {
  messages: IndividualMessage[];
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  skeletonCount?: number;
}

const RAIL_WIDTH = 24; // px — coluna do stepper (linha central em 12px)

/** Timeline vertical com stepper (bolinhas pulsantes centralizadas na linha). */
export function IndividualMessagesTimeline({
  messages,
  loading,
  emptyMessage = 'Nenhuma mensagem registrada.',
  className,
  skeletonCount = 3,
}: IndividualMessagesTimelineProps) {
  if (loading) {
    return (
      <div className={cn('space-y-3', className)}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Skeleton key={i} className="ml-7 h-16 rounded-xl" />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return <p className={cn('text-sm text-muted-foreground', className)}>{emptyMessage}</p>;
  }

  return (
    <div className={cn('relative', className)}>
      {/* Linha vertical contínua — centralizada no rail */}
      <div
        className="absolute bottom-4 top-4 w-px bg-border/90"
        style={{ left: RAIL_WIDTH / 2 - 0.5 }}
        aria-hidden
      />

      <ul className="m-0 list-none space-y-0 p-0">
        {messages.map((msg, index) => (
          <li
            key={msg._id ?? `msg-${index}`}
            className="relative flex gap-3 pb-5 last:pb-0"
          >
            {/* Stepper — bolinha centralizada na linha */}
            <div
              className="relative z-10 flex shrink-0 items-start justify-center pt-[1.125rem]"
              style={{ width: RAIL_WIDTH }}
            >
              <span className="relative flex size-3.5 items-center justify-center">
                <span
                  className="absolute inline-flex size-full animate-ping rounded-full bg-primary/45"
                  style={{ animationDuration: '2.4s', animationDelay: `${index * 0.35}s` }}
                />
                <span className="relative size-2.5 rounded-full bg-primary ring-[3px] ring-background" />
              </span>
            </div>

            <Card className="min-w-0 flex-1 border-border/60 p-3.5 shadow-sm transition-shadow hover:shadow-md">
              <p className="text-xs text-muted-foreground">
                {msg.updated ? `${formatDate(msg.updated)} · ${formatTime(msg.updated)}` : '—'}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed">{msg.description}</p>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function IndividualMessagesTimelineHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}
